import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import { toast } from "react-hot-toast";

import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import CoffeeIcon from "@mui/icons-material/Coffee";
import DoneAllIcon from "@mui/icons-material/DoneAll";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { convertLocalDateTimeToUTC } from "../../../../components/dateTimeUtils";

// Modals
import FaceScanModal from "./faceScanModal";
import MapModal from "./MapModal";

export default function TimeClock({ onPunchSuccess }) {
  const [time, setTime] = useState(new Date());

  const [lastAction, setLastAction] = useState("CLOCK_OUT"); // will sync from backend
  const [selectedAction, setSelectedAction] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Add loading state

  const { t } = useTranslation();

  // Modal states
  const [faceModalOpen, setFaceModalOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  // Data captured
  const [selfieUrl, setSelfieUrl] = useState(null);

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  // keep clock ticking
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // format clock
  const formatTime = (date) => date.toLocaleTimeString("en-GB");

  // fetch today's attendance to set last action
useEffect(() => {
  const fetchAttendanceStatus = async () => {
    try {
      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const todayLocal = dayjs().format("YYYY-MM-DD");
      const dateParam =
        convertLocalDateTimeToUTC(`${todayLocal}T00:00:00`) || todayLocal;

      const res = await fetch(
        `${BASE_URL}/attendance/today?date=${encodeURIComponent(dateParam)}&tz=${encodeURIComponent(userTimezone)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();

      if (res.ok && !data.error) {
        const backendLastAction = data?.data?.dailySummary?.lastPunch?.actionType;
        console.log("✅ Backend lastAction:", backendLastAction);

        if (backendLastAction) {
          setLastAction(backendLastAction);
        } else {
          setLastAction("CLOCK_OUT");
        }
      } else {
        console.warn("⚠️ Could not fetch attendance, defaulting to CLOCK_OUT");
        setLastAction("CLOCK_OUT");
      }
    } catch (err) {
      console.error("❌ Failed to fetch attendance:", err);
      setLastAction("CLOCK_OUT");
    }
  };

  if (token) fetchAttendanceStatus();
}, [token]);
  // Punch API call
  const handlePunch = async ({geo,address}) => {
    console.log("👉 handlePunch called with action:", selectedAction);
    console.log("Current selfieUrl:", selfieUrl);
    console.log(
      "📍 User selected Geo Location:",
      geo?.lat,
      geo?.lng,
      "Accuracy:",
      geo?.accuracy_m
    );

    if (!selfieUrl || !geo || !selectedAction) {
      console.warn("⚠️ Missing required data:", {
        selfieUrl,
        geo,
        selectedAction,
      });
      toast.error("Face, location, and action required!");
      return;
    }

    const payload = {
      action_type: selectedAction,
      gps_lat: geo.lat?.toString(),
      gps_lng: geo.lng?.toString(),
      gps_accuracy: geo.accuracy_m?.toString() || "100",
      selfie: selfieUrl,
      device_timestamp: new Date().toISOString(),
      source: "web",
      app_version: "1.0.0",
      device_id: "test-device-1236",
    locationText: address,
    };

    console.log("📦 Final Payload to send:", payload);

    try {
      setIsLoading(true); // Start loading
      const res = await fetch(`${BASE_URL}/attendance/punch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const rawResponse = await res.text();
      console.log("📥 Raw API response:", rawResponse);

      let data;
      try {
        data = JSON.parse(rawResponse);
      } catch (err) {
        console.error("❌ Failed to parse JSON:", err);
        toast.error("Invalid server response");
        setIsLoading(false);
        return;
      }

      if (res.ok && !data.error) {
toast.success(t(`timeClockAlerts.${selectedAction}`));
        console.log("✅ API Success:", data);
         
        setLastAction(selectedAction); // update state to new action
        setSelfieUrl(null); // Clear selfie after successful punch
        if (onPunchSuccess) onPunchSuccess();
      } else {
        toast.error(data.message || `${selectedAction} failed`);
        console.error("❌ API Failure:", data);
      }
    } catch (err) {
      console.error("❌ Network/API error:", err);
      toast.error("Error while punching attendance");
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  // Buttons
  const renderButtons = () => {
    console.log("🕒 renderButtons called. Current lastAction:", lastAction);

    switch (lastAction) {
      case "CLOCK_IN":
        return (
          <>
            {/* Start Break */}
            <CustomButton
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CoffeeIcon fontSize="small" />
                  {isLoading ? t("loading") : t("timeClock.startBreak")}
                </Box>
              }
              size="small"
              disabled={isLoading}
              onClick={() => {
                setSelectedAction("BREAK_START");
                setFaceModalOpen(true);
              }}
            />

            {/* Clock Out */}
            <CustomButton
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LogoutIcon fontSize="small" />
                  {isLoading ? t("loading") : t("timeClock.checkout")}
                </Box>
              }
              size="small"
              disabled={isLoading}
              onClick={() => {
                setSelectedAction("CLOCK_OUT");
                setFaceModalOpen(true);
              }}
            />
          </>
        );

      case "BREAK_START":
        return (
          <>
            {/* End Break */}
            <CustomButton
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DoneAllIcon fontSize="small" />
                  {isLoading ? t("loading"): t("timeClock.endBreak")}
                </Box>
              }
              size="small"
              disabled={isLoading}
              onClick={() => {
                setSelectedAction("BREAK_END");
                setFaceModalOpen(true);
              }}
            />
          </>
        );

      case "BREAK_END":
        return (
          <>
            {/* Start Break again */}
            <CustomButton
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CoffeeIcon fontSize="small" />
                  {isLoading ?t("loading") : t("timeClock.startBreak")}
                </Box>
              }
              size="small"
              disabled={isLoading}
              onClick={() => {
                setSelectedAction("BREAK_START");
                setFaceModalOpen(true);
              }}
            />

            {/* Clock Out */}
            <CustomButton
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LogoutIcon fontSize="small" />
                  {isLoading ? ("loading") : t("timeClock.checkout")}
                </Box>
              }
              size="small"
              disabled={isLoading}
              onClick={() => {
                setSelectedAction("CLOCK_OUT");
                setFaceModalOpen(true);
              }}
            />
          </>
        );

      case "CLOCK_OUT":
      default:
        return (
          <>
            {/* Clock In */}
            <CustomButton
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LoginIcon fontSize="small" />
                  {isLoading ? t("loading") : t("timeClock.clockin")}
                </Box>
              }
              size="small"
              disabled={isLoading}
              onClick={() => {
                setSelectedAction("CLOCK_IN");
                setFaceModalOpen(true);
              }}
            />
          </>
        );
    }
  };

  const [assignedLocation, setAssignedLocation] = useState(null);

useEffect(() => {
  const fetchAssignedLocation = async () => {
    try {
      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const todayLocal = dayjs().format("YYYY-MM-DD");
      const dateParam =
        convertLocalDateTimeToUTC(`${todayLocal}T00:00:00`) || todayLocal;

      const res = await fetch(
        `${BASE_URL}/attendance/today?date=${encodeURIComponent(dateParam)}&tz=${encodeURIComponent(userTimezone)}`,
        {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && !data.error) {
        const loc = data?.data?.worker?.attendanceSetting?.location;
        console.log("📍 Assigned Location from API:", loc);
        if (loc?.lat && loc?.lng) {
          setAssignedLocation({ lat: loc.lat, lng: loc.lng, radius: loc.radiusMeters || 50 });
        }
      } else {
        console.warn("⚠️ No assigned location found in API:", data);
      }
    } catch (err) {
      console.error("❌ Failed fetching assigned location:", err);
    }
  };
  if (token) fetchAssignedLocation();
}, [token]);


  return (
    <>
      <Box
        sx={{
          maxWidth: 450,
          px: 2,
          backgroundColor: "white",
          borderRadius: 3,
          border: "1px solid #E4E6EF",
          py: 2,
        }}
      >
        <Box>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 8,
            }}
          >
            <CustomText
              sx={{
                ...textStyles.h1,
                fontSize: 16,
                fontWeight: 600,
                color: "#363333",
              }}
            >
              {t("timeClock.title")}
            </CustomText>

<LocalizationProvider dateAdapter={AdapterDayjs}>
  <DatePicker
    value={dayjs()}
    disabled
    format="YYYY-MM-DD"
    maxDate={dayjs()}

    slots={{
      openPickerIcon: () => null,     // remove calendar
      inputAdornment: () => null,     // remove icon wrapper
    }}

    slotProps={{
      textField: {
        size: "small",
        InputProps: {
          readOnly: true,
          sx: {
            padding: 1,
            height: 30,                 // 🚀 SMALL HEIGHT
            fontSize: "16px",           // 🚀 SMALL FONT
          },
        },
        sx: {
          "& .MuiInputBase-root": {
            height: 30,                 // input height
            fontSize: "13px",
          },
          "& .MuiInputBase-input": {
            padding: "6 6px !important", // small padding
            fontSize: "13px",            // small font
          },
        },
      },

      // Disable calendar popup
      popper: { sx: { display: "none" } },
      openPickerButton: { sx: { display: "none" } },
    }}
  />
</LocalizationProvider>






          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box
              sx={{
                width: "40%",
                aspectRatio: "1 / 1",
                borderRadius: "50%",
                backgroundColor: "#1976d2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 5,
                boxShadow: `
                  0 0 0 15px rgba(25, 118, 210, 0.1),
                  0 0 0 30px rgba(25, 118, 210, 0.15),
                  0 0 0 45px rgba(25, 118, 210, 0.2),
                  0 0 0 60px rgba(25, 118, 210, 0.25)
                `,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  color: "white",
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 40, mb: 1 }} />
                {/* <Typography variant="h6">{formatTime(time)}</Typography> */}
              </Box>
            </Box>
          </Box>

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 13 }}>
            {renderButtons()}
          </Box>
        </Box>
      </Box>

      {/* Face Modal */}
      <FaceScanModal
        open={faceModalOpen}
        onClose={() => setFaceModalOpen(false)}
        onContinue={({ face_file_id }) => {
          console.log("📤 FaceModal returned face_file_id:", face_file_id);
          setSelfieUrl(face_file_id);
          setFaceModalOpen(false);
          setMapModalOpen(true); // open map after face
        }}
      />


<MapModal
  open={mapModalOpen}
  onClose={() => {
    if (!isLoading) setMapModalOpen(false); // Prevent closing while loading
  }}
  onContinue={(payload) => {
    console.log("📤 MapModal returned geo payload:", payload);
    setMapModalOpen(false);
    // handlePunch(payload.geo);
    handlePunch(payload);
  }}
/>
    </>
  );
}



















